import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux';
import { useAuth } from '@clerk/clerk-react';
import { ChevronUp, ChevronDown, Info } from 'lucide-react'
import { formatDateInput } from '../../utils/formatter'
import AddToProjectModal from '../Project/AddToProjectModal';
import { updateTaskService } from '../../utils/taskService';
import { saveTask } from '../../reducers/taskReducer';

const TaskFields = ({ task: initialTask, onUpdateTask, ampSection }) => { 
  const { getToken, userId } = useAuth();
  const dispatch = useDispatch();
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [localTask, setLocalTask] = useState(initialTask)
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const categories = ['Administrative & Miscellaneous', 'Communication & Outreach', 'Financial Management', 'Legal & Compliance', 'Research & Analysis', 'Scheduling & Coordination', 'Technical & Product Development']

  useEffect(() => {
    setLocalTask(initialTask)
  }, [initialTask])

  const handleChange = (e) => {
    const { name, value } = e.target
    const updatedTask = { ...localTask, [name]: value }
    setLocalTask(updatedTask)

    if (onUpdateTask) {
      onUpdateTask(updatedTask)
    }
  }

  const handleCategoryChange = async (category) => {
    const updatedTask = { 
      ...localTask, 
      category: localTask.category.includes(category) 
        ? localTask.category.filter(c => c !== category) 
        : [...localTask.category, category] 
    }
    await updateTaskService(getToken, userId, updatedTask)
    dispatch(saveTask({ task: updatedTask }));
    setLocalTask(updatedTask)

    if (onUpdateTask) {
      onUpdateTask(updatedTask)
    }
  }

  const handleStatusChange = async (status) => {
    const updatedTask = { ...localTask, status: status }
    await updateTaskService(getToken, userId, updatedTask)
    dispatch(saveTask({ task: updatedTask }));
    setLocalTask(updatedTask)

    if (onUpdateTask) {
      onUpdateTask(updatedTask)
    }
  }

  const triggerAddItemToProject = async (projectId, projectTitle) => {
    const updatedTask = { ...localTask, metaData: { ...localTask.metaData, projectId, projectTitle } }
    await updateTaskService(getToken, userId, updatedTask)
    dispatch(saveTask({ task: updatedTask }));
    setLocalTask(updatedTask)

    if (onUpdateTask) {
      onUpdateTask(updatedTask)
    }
    setIsProjectModalOpen(false)
  }

  if (!localTask) {
    return null
  }

  return (
    <>
      {/* Primary Fields */}
      <div className={`space-y-4 ${localTask.completed || localTask.archived ? 'opacity-50 pointer-events-none' : ''}`}>
        <div>
          <label className="block text-base text-gray-500 mb-1">Task Title</label>
          <input
            type="text"
            name="title"
            value={localTask.title}
            onChange={handleChange}
            data-amp-track-section={ampSection}
            data-amp-track-action="Task_Title_Change"
            data-amp-track-description="Task Title Change"
            placeholder="Task Title"
            className="w-full text-3xl font-medium px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0"
          />
        </div>
        
        <div>
          <label className="block text-base text-gray-500 mb-1">Task Description</label>
          <textarea
            name="description"
            value={localTask.description}
            onChange={handleChange}
            data-amp-track-section={ampSection}
            data-amp-track-action="Task_Description_Change"
            data-amp-track-description="Task Description Change"
            placeholder="Describe your task..."
            rows="5"
            className="w-full text-base px-3 py-3 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0"
          />
        </div>

        <div className="border-t border-gray-200 pt-6">
          <label className="flex flex-row items-center text-base text-gray-500 mb-2">
            Train Prince Geoffrey
            <Info size={16} className="ml-1 cursor-pointer" onClick={() => setShowInfo(!showInfo)} />
          </label>
          {showInfo && (
            <div className="mt-2 text-sm text-gray-500">
              Train Geoffrey by adding contacts, emails, documents, and other information that will help him complete this Task without you needing to be there.
            </div>
          )}
          <textarea
            name="notes"
            value={localTask.notes}
            onChange={handleChange}
            data-amp-track-section={ampSection}
            data-amp-track-action="Task_Instructions_Change"
            data-amp-track-description="Task Instructions Change"
            placeholder="Write additional task instructions here..."
            rows="4"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-0"
          />
        </div>


        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-500 mb-1">Start By</label>
            <input
              type="date"
              name="startBy"
              value={formatDateInput(localTask.startBy)}
              onChange={handleChange}
              data-amp-track-section={ampSection}
              data-amp-track-action="Task_Start_Date_Change"
              data-amp-track-description="Task Start Date Change"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-0"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-500 mb-1">End By</label>
            <input
              type="date"
              name="endBy"
              value={formatDateInput(localTask.endBy)}
              onChange={handleChange}
              data-amp-track-section={ampSection}
              data-amp-track-action="Task_End_Date_Change"
              data-amp-track-description="Task End Date Change"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-0"
            />
          </div>
        </div>
      </div>

      {/* Separator and Toggle */}
      <div className={`border border-gray-200 rounded-lg p-4 ${localTask.completed || localTask.archived ? 'opacity-50 pointer-events-none' : ''}`}>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          data-amp-track-section={ampSection}
          data-amp-track-action="Toggle_Advanced_Options_Click"
          data-amp-track-description={`${showAdvanced ? 'Hide' : 'Show'} Advanced Options`}
          className="w-full flex items-center justify-between p-0 m-0 text-gray-500 bg-transparent hover:bg-transparent hover:text-gray-700 transition-colors"
        >
          <span className="text-sm font-bold">Advanced Options</span>
          {showAdvanced ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {/* Advanced Fields */}
        {showAdvanced && (
          <div className="space-y-4 pt-4 border-t border-gray-100">
            <div className="flex flex-col gap-2 border-t border-gray-200 pt-4">
              <label className="block text-sm text-gray-500 mb-2">Estimated Time for Completion</label>
              <input
                type="text"
                name="estimation"
                value={localTask.estimation}
                onChange={handleChange}
                data-amp-track-section={ampSection}
                data-amp-track-action="Task_Estimation_Change"
                data-amp-track-description="Task Estimation Change"
                placeholder="e.g., 2 hours"
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-0"
              />
            </div>

            <div className="flex flex-col gap-2 border-t border-gray-200 pt-4">
              <label className="block text-sm text-gray-500 mb-2">Type of Task</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    data-amp-track-section={ampSection}
                    data-amp-track-action="Task_Category_Click"
                    data-amp-track-description={`${category} Category Click`}
                    className={`px-3 py-1 rounded-full text-sm ${
                      localTask.category?.includes(category)
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2 border-t border-gray-200 pt-4">
              <label className="block text-sm text-gray-500 mb-2">Add Task to Project</label>

              {!localTask.metaData?.projectId ? (
                <div className="flex items-center gap-2">
                <button onClick={() => setIsProjectModalOpen(true)} 
                  data-amp-track-section={ampSection}
                  data-amp-track-action="Select_Project_Click"
                  data-amp-track-description="Select a Project"
                  className="px-2 py-1 text-sm m-0 mr-2 bg-stone-700 hover:bg-stone-800">
                  Select a Project
                </button> 
              </div>
              ) : (
                <div>
                  <button onClick={() => setIsProjectModalOpen(true)} 
                    data-amp-track-section={ampSection}
                    data-amp-track-action="Switch_Project_Click"
                    data-amp-track-description="Switch Project"
                    className="px-2 py-1 text-sm m-0 mr-2 bg-stone-700 hover:bg-stone-800">
                    Switch Project
                  </button>
                  <span className="text-sm text-gray-500">{localTask.metaData?.projectTitle}</span>
                </div>
              )}
            </div>
              
            {isProjectModalOpen && (
              <AddToProjectModal setIsProjectModalOpen={setIsProjectModalOpen} addItemToProject={triggerAddItemToProject} />
            )}

            <div className="flex border-t border-gray-200 items-center gap-3 pt-6">
              <div className="flex gap-2 justify-end items-center">
                <label className="text-sm text-gray-500">
                  Override Status
                </label>
                <select
                  data-amp-track-section={ampSection}
                  data-amp-track-action="Task_Status_Change"
                  data-amp-track-description="Task Status Change"
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 bg-white hover:bg-gray-100 transition-colors"
                  defaultValue={localTask.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  <option value="" disabled>Select a Status</option>
                  <option value="ready" defaultValue={localTask.status === 'ready' ? 'ready' : ''}>Ready</option>
                  <option value="on-hold" defaultValue={localTask.status === 'on-hold' ? 'on-hold' : ''}>On Hold</option>
                  <option value="cancelled" defaultValue={localTask.status === 'cancelled' ? 'cancelled' : ''}>Cancelled</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default TaskFields